/**
 * Toolbar.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Chart toolbar component — timeframe tabs, chart type pills, live price,
 * RSI display, extended hours toggle, intraday resolution, screenshot button,
 * analyst overlay toggle.
 *
 * Architecture rules:
 *   - Zero internal state — all state lives in PriceChart.jsx
 *   - Zero logRef — logging is in the callbacks passed from PriceChart.jsx
 *   - Props in, opaque callbacks out
 *   - Adding a new toolbar button = edit only this file
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { TABS, CHART_TYPES } from '../../constants/chartConfig';
import { fmtPrice }          from '../../utils/chartUtils';

const s = {
  pill: (active, color = '#00d4ff') => ({
    padding: '3px 10px', borderRadius: 5, fontFamily: 'inherit',
    fontSize: 11, fontWeight: active ? 700 : 500, cursor: 'pointer',
    border: `1px solid ${active ? `${color}66` : 'rgba(255,255,255,0.08)'}`,
    background: active ? `${color}18` : 'transparent',
    color: active ? color : '#6b7280',
  }),
  divider: { width: 1, height: 16, background: 'rgba(255,255,255,0.08)', margin: '0 2px' },
  iconBtn: {
    padding: '2px 7px', borderRadius: 4, fontFamily: 'inherit',
    fontSize: 11, cursor: 'pointer',
    border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: '#4a4a6a',
  },
};

export default function Toolbar({
  // Timeframe
  activeTab, onTabChange,
  // Chart type
  chartType, onChartTypeChange,
  // Intraday (1D tab only)
  intraRes, onIntraResChange,
  showExtHours, onExtHoursToggle,
  isLoggedIn,
  // Renko brick
  isRenko, renkoBrickAtr, renkoBrickOverride, onRenkoBrickChange,
  // Price display
  lastBar, priceChange, rsiValue, rsiPrev, wsStatus, crosshairLocked,
  // Actions
  onScreenshot, onExpandClick,
  // Analyst overlay (S27K)
  analystData, analystVisible, onAnalystToggle,
  // Mode config
  modeConfig,
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)', flexWrap: 'wrap' }}>

      {/* Timeframe tabs */}
      {modeConfig.pills && TABS.map((t, i) => (
        <button key={t.label} onClick={() => onTabChange(i)} style={s.pill(i === activeTab)}>
          {t.label}
        </button>
      ))}

      <div style={s.divider} />

      {/* Chart type pills */}
      {CHART_TYPES.map(ct => (
        <button key={ct.key} onClick={() => onChartTypeChange(ct.key)} title={ct.label}
          style={s.pill(ct.key === chartType, ct.key === 'renko' ? '#f59e0b' : '#00d4ff')}>
          {ct.shortLabel}
        </button>
      ))}

      {/* 1D — intraday resolution + Ext toggle */}
      {activeTab === 0 && (
        <>
          <div style={s.divider} />
          {(isLoggedIn ? ['1','3','5','15','30'] : ['5','15']).map(r => (
            <button key={r} onClick={() => onIntraResChange(r)}
              style={s.pill(intraRes === r)}>
              {r}m
            </button>
          ))}
          <div style={s.divider} />
          <button onClick={onExtHoursToggle} title={showExtHours ? 'Showing extended hours' : 'Regular session only'}
            style={s.pill(showExtHours, '#f59e0b')}>
            Ext
          </button>
        </>
      )}

      {/* 1W — locked 1H label */}
      {activeTab === 1 && (
        <>
          <div style={s.divider} />
          <span style={{ fontSize: 10, color: '#4a4a6a', padding: '2px 6px', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 5 }}>1H</span>
        </>
      )}

      {/* Renko brick size */}
      {isRenko && renkoBrickAtr != null && (
        <span style={{ fontSize: 10, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5 }}>
          Brick: ${(renkoBrickOverride || renkoBrickAtr).toFixed(2)}{renkoBrickOverride ? ' (custom)' : ' (ATR-14)'}
          <input type="number" placeholder="override" min={0.01} step={0.01}
            value={renkoBrickOverride || ''}
            onChange={e => { const v = parseFloat(e.target.value); onRenkoBrickChange(isNaN(v) || v <= 0 ? null : v); }}
            style={{ width: 58, padding: '2px 4px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, color: '#f59e0b', fontSize: 10, fontFamily: 'inherit' }} />
        </span>
      )}

      {/* Right side — price + actions */}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>

        {/* Analyst overlay pill (S27K) */}
        {analystData?.priceTarget && (
          <button onClick={onAnalystToggle}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all .15s', backdropFilter: 'blur(8px)', background: analystVisible ? 'rgba(167,139,250,0.15)' : 'rgba(255,255,255,0.04)', border: `1px solid ${analystVisible ? 'rgba(167,139,250,0.5)' : 'rgba(255,255,255,0.1)'}`, color: analystVisible ? '#a78bfa' : '#6b7280' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: analystVisible ? '#a78bfa' : '#4a4a6a', flexShrink: 0 }} />
            Analysts
          </button>
        )}

        {/* Live price + change */}
        {lastBar && (
          <span style={{ fontSize: 12, fontWeight: 700, color: priceChange?.change >= 0 ? '#10b981' : '#ef4444' }}>
            {fmtPrice(lastBar.close)}
            {priceChange && (
              <span style={{ fontSize: 10, marginLeft: 4 }}>
                {priceChange.change >= 0 ? '+' : ''}{priceChange.change.toFixed(2)} ({priceChange.pct.toFixed(2)}%)
              </span>
            )}
            {/* WebSocket live dot */}
            {wsStatus === 'live' && (
              <span title="Live data" style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#10b981', marginLeft: 6, boxShadow: '0 0 4px #10b981', verticalAlign: 'middle' }} />
            )}
            {wsStatus === 'connecting' && (
              <span title="Connecting…" style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', marginLeft: 6, verticalAlign: 'middle' }} />
            )}
            {/* Crosshair lock indicator */}
            {crosshairLocked && (
              <span title="Crosshair locked — press L to unlock" style={{ marginLeft: 6, fontSize: 9, color: '#f59e0b', fontWeight: 700, border: '1px solid rgba(245,158,11,0.4)', borderRadius: 3, padding: '0 4px', verticalAlign: 'middle' }}>
                🔒 LOCKED
              </span>
            )}
          </span>
        )}

        {/* RSI display */}
        {rsiValue != null && (
          <span style={{ fontSize: 10, color: rsiValue > 70 ? '#ef4444' : rsiValue < 30 ? '#10b981' : '#8b8ba7' }}>
            RSI {rsiValue.toFixed(1)}
            {rsiPrev != null && (
              <span style={{ marginLeft: 2, fontSize: 9 }}>
                {rsiValue > rsiPrev ? '↑' : rsiValue < rsiPrev ? '↓' : '→'}
              </span>
            )}
          </span>
        )}

        {/* Screenshot */}
        <button onClick={onScreenshot} title="Save chart as image" style={s.iconBtn}>📷</button>

        {/* Expand to fullscreen (embedded mode only) */}
        {modeConfig.sidebar && onExpandClick && (
          <button onClick={onExpandClick} title="Open advanced chart"
            style={{ padding: '2px 8px', borderRadius: 4, fontFamily: 'inherit', fontSize: 10, cursor: 'pointer', border: '1px solid rgba(0,212,255,0.2)', background: 'rgba(0,212,255,0.06)', color: '#00d4ff' }}>
            ⤢
          </button>
        )}
      </div>
    </div>
  );
}
